import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Divider from "@mui/material/Divider";
import Snackbar from "@mui/material/Snackbar";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import ThemedTypography from "./ThemedTypography";
import ThemedTextField from "./ThemedTextField";
import MobileEditAccountDialog from "./dialogs/MobileEditAccountDialog";
import ThemedIconButton from "./ThemedIconButton";
import ClearIcon from "@mui/icons-material/Clear";
import { decoratedFetch } from "../req_utils";
import {
  GENESIS_LOGO_COLOR,
  DARK_THEME_INPUT_OUTLINE_COLOR,
  DARK_THEME_TEXT_COLOR,
  DARK_THEME_DISABLED_TEXT_COLOR,
  DARK_THEME_BODY_COLOR,
  LIGHT_THEME_BODY_COLOR,
} from "../constants";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

const MobileAccountList = ({ openCreateAccountModal }) => {
  const darkThemeEnabled = useSelector((state) => state.darkThemeEnabled);
  const employee_branch_id = useSelector((state) => state.employee_branch_id);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [accountDialogState, setAccountDialogState] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Debounce the search term with 200ms delay
  const debouncedSearchTerm = useDebounce(searchTerm, 200);

  // Filter accounts based on debounced search term
  const filteredAccounts = accounts.filter((account) => {
    const searchLower = debouncedSearchTerm.toLowerCase();
    return (
      account.name?.toLowerCase().includes(searchLower) ||
      account.account_type?.name?.toLowerCase().includes(searchLower)
    );
  });

  useEffect(() => {
    if (!employee_branch_id) return;
    
    setLoading(true);
    decoratedFetch(`/list_accounts?mobile=true&branch_id=${employee_branch_id}`)
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Unknown error");
        }
        return response.json();
      })
      .then((data) => {
        setLoading(false);
        setAccounts(data);
      })
      .catch((error) => {
        setSnackbarMessage("Failed to load accounts");
        setLoading(false);
        console.error("Error fetching data: ", error);
      });
  }, [employee_branch_id]);

  const handleAccountUpdate = (updatedAccount) => {
    setAccounts((prevAccounts) =>
      prevAccounts.map((acc) =>
        acc.id === updatedAccount.id
          ? {
              ...acc,
              name: updatedAccount.name,
              account_type: updatedAccount.account_type,
              notes: updatedAccount.notes,
            }
          : acc,
      ),
    );
  };

  const handleCreateAccount = (newAccount) => {
    setAccounts((prevAccounts) => [newAccount, ...prevAccounts]);
    setCreateDialogOpen(false);
    setTimeout(
      () => setAccountDialogState({ selectedAccount: newAccount }),
      200,
    );
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 1.5,
          pb: 0.5,
          position: "sticky",
          top: 0,
          zIndex: 1,
          backgroundColor: darkThemeEnabled
            ? DARK_THEME_BODY_COLOR
            : LIGHT_THEME_BODY_COLOR,
        }}
      >
        <ThemedTextField
          size="small"
          fullWidth
          variant="outlined"
          placeholder="Search accounts..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 40, // Reduced height
              fontSize: "0.875rem", // Slightly smaller font
            },
            "& .MuiInputAdornment-root": {
              marginRight: 0.5, // Reduce spacing around search icon
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: "1.2rem" }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <ThemedIconButton
                  aria-label="clear search"
                  size="small"
                  onClick={() => setSearchTerm("")}
                  sx={{ p: 0.5 }}
                >
                  <ClearIcon fontSize="small" />
                </ThemedIconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <MobileEditAccountDialog
        parentState={accountDialogState}
        createDialogOpen={createDialogOpen}
        handleClose={() => setAccountDialogState(null)}
        handleCreateClose={() => setCreateDialogOpen(false)}
        onAccountUpdate={handleAccountUpdate}
        onCreateAccount={handleCreateAccount}
      />
      <Snackbar
        sx={{
          position: "fixed",
          left: 8,
          right: 8,
          bottom: 64, // 8px closer to bottom nav, with margin on sides
          zIndex: (theme) => theme.zIndex.snackbar,
          ".MuiSnackbarContent-root": {
            backgroundColor: GENESIS_LOGO_COLOR,
            minWidth: 0,
          },
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage("")}
        message={snackbarMessage}
      />
      {filteredAccounts?.length ? (
        <List sx={{ py: 0, flex: 1, pb: 2 }}>
          {filteredAccounts.map((account, index) => (
            <>
              <ListItemButton
                key={index}
                onClick={() =>
                  setAccountDialogState({
                    selectedAccount: filteredAccounts[index],
                  })
                }
              >
                <ListItemText
                  primary={account.name}
                  secondary={
                    <p style={{ marginTop: "2px", marginBottom: 0 }}>
                      <strong>{"Type: "}</strong>
                      {account.account_type.name}
                    </p>
                  }
                  primaryTypographyProps={{
                    sx: {
                      fontWeight: "bold",
                      color: darkThemeEnabled
                        ? DARK_THEME_TEXT_COLOR
                        : "inherit",
                    },
                  }}
                  secondaryTypographyProps={{
                    sx: {
                      color: darkThemeEnabled
                        ? DARK_THEME_DISABLED_TEXT_COLOR
                        : "inherit",
                    },
                  }}
                />
              </ListItemButton>
              {index < filteredAccounts.length - 1 && (
                <Divider
                  component="li"
                  sx={{
                    backgroundColor: darkThemeEnabled
                      ? DARK_THEME_INPUT_OUTLINE_COLOR
                      : "inherited",
                  }}
                />
              )}
            </>
          ))}
        </List>
      ) : loading ? (
        <Box
          sx={{
            alignSelf: "center",
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size="50px" />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ThemedTypography>
            {searchTerm ? "No accounts found" : "No accounts"}
          </ThemedTypography>
        </Box>
      )}
      <Fab
        color="primary"
        aria-label="add"
        size="medium"
        sx={{ position: "fixed", bottom: 64, right: 16 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default MobileAccountList;
